// opervex-frontend/src/layout/Sidebar/Navigation.js

import { useMemo, useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import navConfig from "./navigation.config";
import { useAuth } from "../../contexts/AuthContext";

/**
 * ============================
 * FILTRAR NAVEGAÇÃO POR ROLES
 * ============================
 */
const buildPath = (base, segment) => {
  const cleanBase = base.replace(/\/+$/, "");
  const cleanSegment = segment?.replace(/^\/+/, "");

  return cleanSegment ? `${cleanBase}/${cleanSegment}` : cleanBase;
};

const filterNavigation = (navigation = [], roles = [], user = {}) => {
  if (!Array.isArray(navigation)) return [];

  const safeRoles = Array.isArray(roles) ? roles : [];

  return navigation.reduce((filtered, item) => {
    // HEADER AGORA PODE TER CHILDREN
    if (item.kind === "header") {
      const children = filterNavigation(item.children || [], safeRoles, user);

      if (children.length > 0) {
        filtered.push({ ...item, children });
      }

      return filtered;
    }

    // Regra por command
    if (item.command && !safeRoles.some((r) => r.command === item.command)) {
      return filtered;
    }

    // Regra administrador
    if (item.administrator && !user?.administrator) {
      return filtered;
    }

    // Submenus
    if (item.children?.length) {
      const children = filterNavigation(item.children, safeRoles, user);
      if (children.length > 0) {
        filtered.push({ ...item, children });
      }
    } else {
      filtered.push(item);
    }

    return filtered;
  }, []);
};

/**
 * ============================
 * SUBMENU RECURSIVO
 * ============================
 */
const SubMenu = ({ item, basePath, level = 1 }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const hasActiveChild = (children, base) => {
    return children.some((child) => {
      const fullPath = child.segment ? `${base}/${child.segment}` : base;

      if (location.pathname === fullPath) return true;

      if (child.children) {
        return hasActiveChild(child.children, fullPath);
      }

      return false;
    });
  };

  useEffect(() => {
    if (hasActiveChild(item.children, basePath)) {
      setOpen(true);
    }
  }, [location.pathname]); // eslint-disable-line

  return (
    <>
      <div className={`sidebar-item submenu-toggle ${open ? "open" : ""}`} onClick={() => setOpen((prev) => !prev)}>
        <span className="submenu-left">
          {item.icon}
          <span>{item.title}</span>
        </span>

        <span className={`submenu-arrow ${open ? "rotate" : ""}`}>▸</span>
      </div>

      <div className={`submenu-wrapper ${open ? "expanded" : ""}`}>
        {item.children.map((child, i) =>
          child.children ? (
            <SubMenu key={i} item={child} basePath={buildPath(basePath, child.segment)} level={level + 1} />
          ) : (
            <NavLink
              key={i}
              to={buildPath(basePath, child.segment)}
              data-level={level}
              className={({ isActive }) => (isActive ? "sidebar-subitem active" : "sidebar-subitem")}
            >
              {child.icon}
              <span>{child.title}</span>
            </NavLink>
          ),
        )}
      </div>
    </>
  );
};

/**
 * ============================
 * NAVIGATION PRINCIPAL
 * ============================
 */
const Navigation = () => {
  const { roles, user, tenantId } = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const safeRoles = Array.isArray(roles) ? roles : [];

  const items = useMemo(() => filterNavigation(navConfig, safeRoles, user), [safeRoles, user]);

  if (!tenantId) return null;

  return (
    <>
      {items.map((item, index) => {
        // HEADER COM CHILDREN
        if (item.kind === "header") {
          return (
            <div key={index} className="sidebar-section">
              <SubMenu item={item} basePath={buildPath(`/${tenantId}`, item.segment)} />
            </div>
          );
        }

        // ITEM COM SUBMENU
        if (item.children) {
          return <SubMenu key={index} item={item} basePath={buildPath(`/${tenantId}`, item.segment)} level={1} />;
        }

        // ITEM SIMPLES
        return (
          <NavLink
            key={index}
            to={buildPath(`/${tenantId}`, item.segment)}
            end={!item.segment}
            className={({ isActive }) => (isActive ? "sidebar-item active" : "sidebar-item")}
          >
            {item.icon}
            <span>{item.title}</span>
          </NavLink>
        );
      })}
    </>
  );
};

export default Navigation;
